<template>
  <div class="container my-4">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h3 class="m-0">💳 Cuentas Cargadas a Habitación</h3>
      <button
        class="btn btn-danger btn-sm"
        @click="descargarPDF"
        :disabled="cuentas.length === 0"
        title="Descargar PDF"
      >
        <i class="bi bi-file-pdf me-1"></i> Descargar PDF
      </button>
    </div>

    <!-- Toast -->
    <div v-if="mostrarToast" class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1055;">
      <div class="toast show align-items-center text-white" :class="toastColor">
        <div class="d-flex">
          <div class="toast-body">
            <i :class="iconoToast" class="me-2"></i> {{ alerta.mensaje }}
          </div>
          <button type="button" class="btn-close btn-close-white me-2 m-auto" @click="cerrarToast"></button>
        </div>
      </div>
    </div>

    <!-- Búsqueda -->
    <div class="card shadow-sm p-4 mb-4">
      <div class="row g-3">
        <div class="col-md-6">
          <label class="form-label fw-semibold">Buscar por:</label>
          <input
            type="text"
            v-model="busqueda"
            @input="filtrarCuentas"
            class="form-control"
            placeholder="Habitación, Cliente, o Código de Reserva..."
          />
        </div>
        <div class="col-md-6 d-flex align-items-end">
          <button class="btn btn-outline-secondary w-100" @click="limpiarBusqueda">
            <i class="bx bx-x"></i> Limpiar
          </button>
        </div>
      </div>
    </div>

    <!-- Lista de cuentas -->
    <div v-if="cuentasFiltradas.length === 0" class="alert alert-info text-center">
      <i class="bx bx-info-circle"></i>
      {{ busqueda ? 'No se encontraron resultados' : 'No hay cuentas cargadas a habitación' }}
    </div>

    <div v-else class="row g-3">
      <div v-for="cuenta in cuentasFiltradas" :key="cuenta.IdCuentaPedido" class="col-12">
        <div class="card shadow-sm hover-shadow">
          <div class="card-body">
            <div class="row align-items-center">
              <!-- Info de la cuenta -->
              <div class="col-md-6">
                <h6 class="text-primary mb-2">
                  <i class="bx bx-receipt"></i> {{ cuenta.NombreCuenta }}
                </h6>
                <p class="mb-1">
                  <i class="bx bx-table"></i> <strong>Mesa:</strong> {{ cuenta.pedido?.mesa?.Numero || 'N/A' }}
                </p>
                <p class="mb-1">
                  <i class="bx bx-door-open"></i> <strong>Habitación:</strong> {{ cuenta.habitacion?.Numero || 'N/A' }}
                </p>
                <p class="mb-1">
                  <i class="bx bx-user"></i> <strong>Huésped:</strong>
                  {{ cuenta.habitacion?.reserva?.huesped?.Nombre }} {{ cuenta.habitacion?.reserva?.huesped?.Apellido }}
                </p>
                <p class="mb-0 text-muted small">
                  <i class="bx bx-calendar"></i> Código Reserva: {{ cuenta.habitacion?.reserva?.CodigoReserva || 'N/A' }}
                </p>
              </div>

              <!-- Montos -->
              <div class="col-md-3 text-center">
                <p class="mb-1"><small class="text-muted">Total</small></p>
                <h5 class="text-success mb-0">Q{{ parseFloat(cuenta.TotalFinal || cuenta.Total).toFixed(2) }}</h5>
                <span class="badge" :class="cuenta.Estado === 0 ? 'bg-warning text-dark' : 'bg-success'">
                  {{ cuenta.Estado === 0 ? 'Pendiente' : 'Pagado' }}
                </span>
              </div>

              <!-- Acciones -->
              <div class="col-md-3 text-end">
                <button
                  v-if="cuenta.Estado === 0"
                  @click="irAPagar(cuenta.IdCuentaPedido)"
                  class="btn btn-primary btn-sm w-100 mb-2"
                >
                  <i class="bx bx-money"></i> Pagar Ahora
                </button>
                <button
                  @click="verDetalles(cuenta.IdCuentaPedido)"
                  class="btn btn-outline-secondary btn-sm w-100"
                >
                  <i class="bx bx-show"></i> Ver Detalles
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_URL } from '@/config/config.js';

export default {
  name: 'CuentasCargadasHabitacion',
  data() {
    return {
      cuentas: [],
      cuentasFiltradas: [],
      busqueda: '',
      mostrarToast: false,
      alerta: { mensaje: '', tipo: 'success' }
    };
  },
  computed: {
    toastColor() {
      return this.alerta.tipo === 'success' ? 'bg-success' : 'bg-danger';
    },
    iconoToast() {
      return this.alerta.tipo === 'success' ? 'bx bx-check-circle' : 'bx bx-x-circle';
    }
  },
  mounted() {
    this.cargarCuentas();
  },
  methods: {
    async cargarCuentas() {
      try {
        const { data } = await axios.get(`${API_URL}/cuentas-cargadas-habitacion`);
        this.cuentas = data;
        this.cuentasFiltradas = data;
      } catch (error) {
        console.error('Error cargando cuentas:', error);
        this.mostrarAlerta('Error al cargar las cuentas', 'danger');
      }
    },
    filtrarCuentas() {
      const termino = this.busqueda.toLowerCase().trim();

      if (!termino) {
        this.cuentasFiltradas = this.cuentas;
        return;
      }

      this.cuentasFiltradas = this.cuentas.filter(cuenta => {
        const numeroHabitacion = (cuenta.habitacion?.Numero || '').toString().toLowerCase();
        const nombreHuesped = `${cuenta.habitacion?.reserva?.huesped?.Nombre || ''} ${cuenta.habitacion?.reserva?.huesped?.Apellido || ''}`.toLowerCase();
        const codigoReserva = (cuenta.habitacion?.reserva?.CodigoReserva || '').toLowerCase();

        return numeroHabitacion.includes(termino) ||
               nombreHuesped.includes(termino) ||
               codigoReserva.includes(termino);
      });
    },
    limpiarBusqueda() {
      this.busqueda = '';
      this.filtrarCuentas();
    },
    irAPagar(idCuenta) {
      this.$router.push(`/pagar-cuenta-pedido/${idCuenta}`);
    },
    verDetalles(idCuenta) {
      this.$router.push(`/pagar-cuenta-pedido/${idCuenta}`);
    },
    mostrarAlerta(mensaje, tipo = 'success') {
      this.alerta = { mensaje, tipo };
      this.mostrarToast = true;
      setTimeout(() => {
        this.mostrarToast = false;
      }, 3000);
    },
    cerrarToast() {
      this.mostrarToast = false;
    },
    async descargarPDF() {
      if (this.cuentas.length === 0) {
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/cuentas-cargadas-habitacion-pdf`, {
          responseType: 'blob'
        });

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        const fecha = new Date().toISOString().split('T')[0];
        link.download = `Cuentas_Cargadas_Habitacion_${fecha}.pdf`;
        link.click();
        window.URL.revokeObjectURL(link.href);
      } catch (error) {
        console.error('Error descargando PDF:', error);
        this.mostrarToastMensaje('Error al generar el PDF', 'danger');
      }
    }
  }
};
</script>

<style scoped>
.hover-shadow {
  transition: box-shadow 0.3s ease;
}

.hover-shadow:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}
</style>
